import { useState } from 'react';
import { format } from 'date-fns';
// @mui
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  Typography,
  Button,
  Chip,
  Hidden,
} from '@mui/material';
// components
import Scrollbar from '../../../components/Scrollbar';
import DocuSealSignDialog from './DocuSealSignDialog';

// ----------------------------------------------------------------------

export type SignatureStatus =
  | 'waiting'
  | 'pending'
  | 'viewed'
  | 'started'
  | 'completed'
  | 'declined';

export type DocumentSigner = {
  id: number;
  user_id: number;
  full_name: string;
  email: string;
  sign_order: number;
  docuseal_signer_role: string;
  status: SignatureStatus;
  signed_at: string | null;
  declined_at: string | null;
};

export type PendingSignatureDocument = {
  id: number;
  name: string;
  description: string;
  file_type: string;
  docuseal_template_id: number;
  deadline_signature_at: string | null;
  signers: DocumentSigner[];
};

export type PendingSignatureItem = {
  id: number;
  status: SignatureStatus;
  sign_order: number;
  signed_at: string | null;
  declined_at: string | null;
  docuseal_submitter_id: string;
  docuseal_signing_url: string;
  document: PendingSignatureDocument;
};

type Props = {
  signatures: PendingSignatureItem[];
  onSigned: (signedId: number) => void;
  onClose?: () => void;
};

// ----------------------------------------------------------------------

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  waiting: 'default',
  pending: 'warning',
  viewed: 'info',
  started: 'info',
  completed: 'success',
  declined: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  waiting: 'Waiting',
  pending: 'Pending',
  viewed: 'Viewed',
  started: 'Started',
  completed: 'Completed',
  declined: 'Declined',
};

const SIGNABLE_STATUSES: string[] = ['pending', 'viewed', 'started'];

// ----------------------------------------------------------------------

export default function PendingSignaturesTable({ signatures, onSigned, onClose }: Props) {
  const [signDialog, setSignDialog] = useState<{
    open: boolean;
    signingUrl: string | null;
    documentName: string;
    signatureId: number | null;
  }>({ open: false, signingUrl: null, documentName: '', signatureId: null });

  const handleSign = (item: PendingSignatureItem) => {
    setSignDialog({
      open: true,
      signingUrl: item.docuseal_signing_url,
      documentName: item.document.name,
      signatureId: item.id,
    });
  };

  const closeDialog = () => {
    setSignDialog({ open: false, signingUrl: null, documentName: '', signatureId: null });
  };

  const handleCloseSignDialog = () => {
    closeDialog();
    onClose?.();
  };

  const handleSignComplete = () => {
    const { signatureId } = signDialog;
    closeDialog();
    if (signatureId !== null) onSigned(signatureId);
  };

  const handleDeclineComplete = () => {
    const { signatureId } = signDialog;
    closeDialog();
    if (signatureId !== null) onSigned(signatureId);
  };

  if (signatures.length === 0) {
    return (
      <Typography sx={{ mb: 4, ml: 1, textAlign: 'center' }} variant="body1">
        No documents awaiting your signature.
      </Typography>
    );
  }

  return (
    <>
      <DocuSealSignDialog
        open={signDialog.open}
        signingUrl={signDialog.signingUrl}
        documentName={signDialog.documentName}
        onClose={handleCloseSignDialog}
        onComplete={handleSignComplete}
        onDeclineComplete={handleDeclineComplete}
      />

      <Scrollbar sx={{ flexGrow: 1 }}>
        <TableContainer sx={{ ...MY_STYLE.table_container }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...MY_STYLE.name_col }}>Document</TableCell>
                <Hidden only={['xs', 'sm']}>
                  <TableCell>Status</TableCell>
                </Hidden>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signatures.map((item) => (
                <TableRow key={item.id} sx={{ td: { borderBottom: 'solid 1px #f4f6f8' } }}>
                  <TableCell sx={{ ...MY_STYLE.name_col }}>
                    <Typography variant="body2" fontWeight={600}>
                      {item.document.name}
                    </Typography>
                    {item.document.description && (
                      <Typography variant="caption" color="text.secondary">
                        {item.document.description}
                      </Typography>
                    )}
                  </TableCell>

                  <Hidden only={['xs', 'sm']}>
                    <TableCell>
                      <Chip
                        label={STATUS_LABEL[item.status] || item.status}
                        color={STATUS_COLOR[item.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                  </Hidden>

                  <TableCell align="right">
                    {SIGNABLE_STATUSES.includes(item.status) && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSign(item)}
                        sx={{ fontSize: '13px' }}
                      >
                        Sign
                      </Button>
                    )}
                    {item.status === 'waiting' && (
                      <Button variant="outlined" disabled sx={{ fontSize: '13px' }}>
                        Waiting
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </>
  );
}

// ----------------------------------------------------------------------

const MY_STYLE = {
  table_container: {
    '@media (min-width: 900px)': {
      minWidth: '720px',
    },
  },
  name_col: {
    '@media (max-width: 899px)': {
      width: '250px',
    },
  },
};
